import { Button } from '@fsd/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { ProductCard } from '@/components';
import { useProducts } from '@/hooks/useProducts';
import { useTranslation } from '@/i18n/hooks';
import { useInView } from '@/utils';

export function NewDrones() {
  const { t, locale } = useTranslation('home');
  const { products: drones, loading } = useProducts('new-drones');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const displayDrones = drones.slice(0, 3);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="new-drones"
      className="relative overflow-hidden bg-white py-24 sm:py-32 dark:bg-slate-900"
    >
      {/* Premium Background Decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Mesh Gradient */}
        <div className="absolute -top-[30%] -left-[10%] h-full w-full rounded-full bg-primary/5 blur-[120px] dark:bg-primary/10"></div>
        <div className="absolute -bottom-[20%] -right-[10%] h-[80%] w-[80%] rounded-full bg-secondary/5 blur-[100px] dark:bg-secondary/10"></div>

        {/* Subtle Grid */}
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150 dark:opacity-10 dark:invert"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className="mb-16 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                {t('newDrones.badge')}
              </div>
              <h2
                className={`text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-100 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
              >
                {t('newDrones.title')}
              </h2>
              <p
                className={`mt-6 text-xl leading-relaxed text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '200ms' }}
              >
                {t('newDrones.subtitle')}
              </p>
            </div>

            <div
              className={`fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '400ms' }}
            >
              <Link to="/catalog" search={{ category: 'new', page: 1 }}>
                <Button
                  size="lg"
                  className="group rounded-full px-8 py-6 text-base font-bold shadow-2xl shadow-primary/20 transition-all hover:scale-105 active:scale-95"
                >
                  {t('newDrones.viewCatalog')}
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="relative">
          {loading ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-[520px] w-full animate-pulse rounded-4xl bg-slate-100 dark:bg-slate-800/50"
                />
              ))}
            </div>
          ) : drones.length === 0 ? (
            <div className="relative overflow-hidden rounded-[3rem] border border-slate-200 bg-slate-50/50 p-12 text-center dark:border-slate-800 dark:bg-slate-900/50 sm:p-24">
              <div className="absolute inset-0 opacity-5 dark:opacity-10">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[24px_24px]"></div>
              </div>
              <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-4xl bg-white text-slate-400 shadow-xl dark:bg-slate-800">
                <svg
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <title>No products</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="relative mt-8 max-w-md mx-auto text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                {t('newDrones.noProducts')}
              </h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {displayDrones.map((drone, index) => (
                <div
                  key={drone.id}
                  className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
                  style={{ transitionDelay: `${300 + index * 150}ms` }}
                >
                  <ProductCard drone={drone} locale={locale} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
